<template>
    <div>
        <loading-progress-bar v-if="loading" :loading="loading" color="secondary"></loading-progress-bar>
        <runtime-template-compiler v-if="!loading&&template" :template="template" :parent="parentComponent"/>
    </div>
</template>

<script>
import {callAPI, getNotification, getMaintenanceRecords} from '@/helper'
import LoadingProgressBar from './../LoadingProgressBar'
import {RuntimeTemplateCompiler} from "vue-runtime-template-compiler"
import moment from "moment";
import unitResolver from "../mixins/unitResolver";

export default {
    props: {
        svg: {type: String, default: null},
        data3query: {type: Object, default: null},
        dateRange: {type: Object, default: null},
        includeIssues: {type: Boolean, default: false},
        includeMaintenance: {type: Boolean, default: false},
    },
    components: {LoadingProgressBar,RuntimeTemplateCompiler},
    mixins:[unitResolver],
    name: 'SvgWidget',
    data: function () {
        return {
            loading: false,
            template: null,
            points: null,
            issues: [],
            maintenanceRecords: [],
            timeout: null,
            parentComponent: this,
        }
    },
    watch: {
        dateRange () {
            if(this.dateRange&&this.svg&&this.svg!==""){
                this.refresh()
            }
        },
    },
    mounted() {
        let me=this
        if(this.dateRange&&this.svg&&this.svg!==""){
            let newTemplate="<div>"+this.svg+"</div>" //to avoid any vue borks due to multiple root elements in component
            let ids=newTemplate.match(/id="(.*?)"/g)
            if(ids&&ids.length>0){
                for (let id of ids){
                    if (id.startsWith('id="carnot--')){
                        let splitId=id.slice(0, -1).split("--")
                        if(!splitId[3]){
                            newTemplate=newTemplate.replaceAll(id,"v-text=\"points['"+splitId[1]+" - "+splitId[2]+"'] ? points['"+splitId[1]+" - "+splitId[2]+"'].value : ''\"")
                        } else if (splitId[3]==="show"){
                            newTemplate=newTemplate.replaceAll(id,"v-show=\"points['"+splitId[1]+" - "+splitId[2]+"']&&points['"+splitId[1]+" - "+splitId[2]+"'].value\"")
                        } else if (splitId[3]==="hide"){
                            newTemplate=newTemplate.replaceAll(id,"v-show=\"!points['"+splitId[1]+" - "+splitId[2]+"']||!points['"+splitId[1]+" - "+splitId[2]+"'].value\"")
                        }
                    }
                }
            }
            me.template=newTemplate
            me.refresh()
        }
    },
    methods: {
        debounce(callback, wait) {
            clearTimeout(this.timeout);
            this.timeout= setTimeout(() => callback(), wait);
        },
        refresh(){
            this.debounce(this.doRefresh,200);
        },
        isFloat(n) {
            return Number(n) === n && n % 1 !== 0;
        },
        async doRefresh() {
            this.loading = true
            if(this.includeIssues){
                let notifResult = await getNotification({
                    facility_name: this.$store.state.pointsListStore.pointsList.facility_name,
                    date_range:[this.dateRange.start,moment(this.dateRange.end).format('YYYY-MM-DD')+" 23:59:59"],
                })
                this.issues = notifResult.result.filter(x=>x.topic!=="anomaly"&&!x.title.toLowerCase().startsWith("anomaly"))
            }
            if(this.includeMaintenance){
                let df = await getMaintenanceRecords({
                    facility_name: this.$store.state.pointsListStore.pointsList.facility_name,
                    date_range:{start:this.dateRange.start,end:moment(this.dateRange.end).format('YYYY-MM-DD')+" 23:59:59"}
                })
                this.maintenanceRecords = df['result']
            }
            if(this.data3query){
                let payloadArray=JSON.parse(JSON.stringify(this.data3query['data-payload']))
                payloadArray=payloadArray.filter(x=>x.filters&&(x.filters.sensor_name||x.filters.equipment_name))
                for (let payload of payloadArray){
                    if(payload.useCustomRange&&payload.customRange){
                        payload.date_range=[payload.customRange.start,payload.customRange.end.length<12 ? payload.customRange.end+" 23:59:59": payload.customRange.end]
                    } else if(this.dateRange){
                        if(payload.offsetRange&&payload.offsetAmount&&payload.offsetType){
                            payload.date_range=[
                                moment(this.dateRange.start).subtract(payload.offsetAmount, payload.offsetType).format('YYYY-MM-DD'),
                                moment(this.dateRange.end).subtract(payload.offsetAmount, payload.offsetType).format('YYYY-MM-DD')+" 23:59:59"]
                        } else {
                            payload.date_range=[this.dateRange.start,this.dateRange.end.length<12 ? this.dateRange.end+" 23:59:59": this.dateRange.end]
                        }
                    }
                    if(!payload.filters.facility_name&&this.$store.state.pointsListStore.pointsList.facility_name){
                        payload.filters.facility_name=this.$store.state.pointsListStore.pointsList.facility_name
                    }
                    if(payload.filters.equipment_name&&this.equipment){
                        payload.filters.equipment_name=this.equipment
                    }
                    if(payload.onOnly){
                        if(!payload.pipeline){
                            payload.pipeline=[]
                        }
                        payload.pipeline.unshift(
                            {
                                "type": "filter",
                                "filter_op": "eq",
                                "filter_by": "* - on",
                                "filter_value": 1,
                            }
                        )
                    }
                }
                let results = await Promise.all(payloadArray.map(p=>callAPI('data3/',p)))
                let newPoints = {}
                for (let result of results){
                    for (let seriesIndex in result.data) {
                        if(seriesIndex==="timestamp"){
                            continue
                        }
                        let equipment_name=seriesIndex.split(" - ")[0]
                        let sensor_name=seriesIndex.split(" - ")[1]
                        equipment_name = equipment_name.replaceAll("%", "_")
                        equipment_name = equipment_name.replaceAll("/", "_")
                        equipment_name = equipment_name.replaceAll("(", "_")
                        equipment_name = equipment_name.replaceAll(")", "_")
                        equipment_name = equipment_name.replaceAll(" ", "_")
                        sensor_name = sensor_name.replaceAll("%", "_")
                        sensor_name = sensor_name.replaceAll("/", "_")
                        sensor_name = sensor_name.replaceAll("(", "_")
                        sensor_name = sensor_name.replaceAll(")", "_")
                        sensor_name = sensor_name.replaceAll(" ", "_")
                        let value=result.data[seriesIndex].reduce((acc, curr) => curr!==null ? curr : acc)
                        newPoints[equipment_name + " - " + sensor_name] = {
                            value: value && this.isFloat(value) ? value.toFixed(2) : value,
                            unit: this.getUnit(seriesIndex.split(" - ")[1],seriesIndex.split(" - ")[0]),
                            equipment_name: equipment_name,
                            sensor_name: sensor_name
                        }
                    }
                }
                this.points = newPoints
            }
            this.loading = false
        },
    },
}
</script>

<style scoped>

</style>
